import {
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl,
  UntypedFormArray,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import * as _ from 'underscore';
import { map, first, tap, catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { NgbNav, NgbModal, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';

// components
import { JobDetailsTabComponent } from './job-details-tab/job-details-tab.component';
import { TaxesTabComponent } from './taxes-tab/taxes-tab.component';
import { PersonalDetailsTabComponent } from './personal-details-tab/personal-details-tab.component';

// models

// services

import { EmergencyContact } from '../../../models/emergency-contact';
import { BaseComponent } from '../../../components/base/base.component';
import { PeopleService } from '../../../services/people.service';
import { SharedService } from '../../../services/shared.service';
import { EmployeeDeduction } from '../../../models/employee-deductions';
import { PayrollService } from '../../../services/payroll.service';
import { CompanyLock, LockTypes } from '../../../models/company-lock';
import { EmployeeEarning } from '../../../models/employee-earning';
import { EmployeeDirectDepositExtended } from '../../../models/employee-direct-deposit';
import { ApiResponse } from '../../../models/api-response';
import { CompaniesService } from '../../../services/companies.service';
import { AuthService } from '../../../services/auth.service';
import {
  Person,
  PersonEditorCompanyConfig,
  PersonEditorEnums,
  PersonEditorForms
} from '../../../models/person';
import { EmployeePTO, PTOConfig } from '../../../models/paid-time-off';
import { ModalService } from '../../../services/modal.service';
import { Client } from '../../../models/client';
import { AuditService } from '../../../services/audit.service';
import { EmploymentTypes } from '../../../models/employment-types';
import { PersonInitService } from '../../../services/person-init.service';
import { FlattenResponseMessages, GetResponseModalStyle } from '../../../utils/api-response-helper';
import { parseDatePickerValue } from '../../../utils/datepicker-value-parser';
import { ModalStates } from '../../../models/modal';
import { PersonValidationService } from '../../../services/person-validation.service';
import { getObjectDeltas } from '../../../utils/object-deltas';
import { EmployeeStatus } from '../../../models/person-status';
import { PersonSaveService } from '../../../services/person-save.service';
import { InstanceTypes } from '../../../models/instance-types';
import { ChangeRequestService } from '../../../services/change-request.service';
import { ClientFeatureWhitelistService } from '../../../services/client-feature-whitelist.service';
import { Select, Store } from '@ngxs/store';
import {
  BuildLocalTaxForm,
  ClearAlertModal,
  ClearEmergencyContacts,
  ClearEmployeeStatuses,
  ClearPerson,
  InitFormData,
  LoadJobDetailsForm,
  LoadPerson,
  LoadPersonalDetailsForm,
  LoadUniversalCompanyConfigs,
  LoadUniversalEnums,
  MarkActiveFormAsTouched,
  MarkFormsPristine,
  RefreshEmployeeStatuses,
  RefreshPersonalDetailsLists,
  ReloadDirectDeposits,
  ReloadEmployeeDeductions,
  ReloadEmployeeEarnings,
  ResetEditorForms,
  ResetEditorState,
  ResetJobDetailsForm,
  ResetPersonalDetailsForm,
  ResetTaxesForm,
  SavePerson,
  SetDirectDeposits,
  SetEditorMode,
  SetEmployeeDeductions,
  SetEmployeeStateTaxExemptionsByState,
  SetFederalIdChanged,
  SetHeader,
  SetJobDetails,
  SetPersonalDetails,
  SetReadOnly,
  SetSaveButtonText,
  SetSaveInProgress,
  SetShowVerifyFederalId,
  SetTabHasChanged,
  SetTaxes,
  SetTaxExemptions,
  SetUseNewHireReporting
} from '../../../state/PersonEditorState/PersonEditor.actions';
import {
  PersonalDetailsFormSelectors,
  PersonEditorState
} from '../../../state/PersonEditorState/PersonEditor.state';
import { SetFormPristine } from '@ngxs/form-plugin';
import { WarningLevel } from '../../../models/error-detail';
import { ToastService } from '../../../services/toast.service';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';

@Component({
  selector: 'person-editor',
  templateUrl: './person-editor.component.html',
  styleUrls: ['./person-editor.component.css'],
  standalone: false
})
export class PersonEditorComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() clientCode: string;
  @Input() personId: number;
  @Input() mode: 'add' | 'edit';
  @Input() addToPayrollRoute: number | string;
  @Input() editViaPayrollGrid: boolean; // overrides the read only flag

  @Output() clientOrPersonNotFound: EventEmitter<any> = new EventEmitter();
  @Output() returnToPayroll: EventEmitter<any> = new EventEmitter();
  @Output() onEditorLeave: EventEmitter<any> = new EventEmitter();

  @ViewChild('nav') private tabs: NgbNav;
  @ViewChild('personalDetailsTab')
  personalDetailsTab: PersonalDetailsTabComponent;
  @ViewChild('jobDetailsTab') jobDetailsTab: JobDetailsTabComponent;
  @ViewChild('taxesTab') taxesTab: TaxesTabComponent;

  @Select(PersonEditorState.getPTOConfigs)
  public ptoConfigs: Observable<PTOConfig[]>;

  //These forms aren't used by anything anymore, however
  //The tab switching logic is still coupled to them, so they need to exist.
  public forms: PersonEditorForms;

  public client: Client;
  public companyLock: CompanyLock;

  @Select(PersonEditorState.isNonW2Employee)
  public isNonW2Employee: Observable<boolean>;

  // view data
  public activeTabIdx = 1;
  @Select(PersonEditorState.getAllowDirectDeposit)
  public allowDirectDeposits: Observable<boolean>;
  public confirmEmptyFederalId: boolean;
  public checkedDupeSSN: boolean;
  public initComplete: boolean; // attempt to reduce page load form element flickering

  public canSubmitRequests: boolean = false;

  @Select(PersonEditorState.getPerson) public person: Observable<Person>;
  @Select(PersonEditorState.getReadOnly)
  public readOnly: Observable<boolean>;
  public tabOrder = {};
  public tabElemIds = {
    1: 'personal-details-tab',
    2: 'job-details-tab',
    3: 'earnings-and-deductions-tab',
    4: 'taxes-tab',
    5: 'benefits-tab',
    6: 'direct-deposit-tab',
    7: 'audit-log-tab'
  };

  get isEmployeePortal() {
    return this.env.instanceType === InstanceTypes.EmployeePortal;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private auditService: AuditService,
    public authService: AuthService,
    private companyService: CompaniesService,
    private modalService: ModalService,
    private payrollService: PayrollService,
    public peopleService: PeopleService,
    public initService: PersonInitService,
    public validationService: PersonValidationService,
    private sharedService: SharedService,
    private changeRequestService: ChangeRequestService,
    private whitelistService: ClientFeatureWhitelistService,
    private store: Store,
    private toastService: ToastService,
    @Inject('env') private env: any
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(PersonEditorState.getAlertModalMessage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        if (message.msg) {
          this.modalService.showAlertModal(message.msg, message.style);
          this.store.dispatch(new ClearAlertModal());
        }
      });
    // first, validate the client Id
    this.store.selectOnce(ClientSelectionState.client).subscribe(
      val => {
        if (val) {
          this.client = val;
          this.store
            .dispatch(new SetUseNewHireReporting(val.UseNewHireReporting || false))
            .subscribe(() => {
              this._init();
            });
        } else {
          // no client exists for given id
          this.clientOrPersonNotFound.emit();
        }
      },
      err => this.clientOrPersonNotFound.emit()
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(new ResetEditorState());
  }

  //#region PUBLIC METHODS

  public cancelChanges(reinit: boolean) {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      switch (this.tabs.activeId) {
        case 'personal-details-tab': {
          if (!person.Id) {
            this.store.dispatch(new ClearEmergencyContacts());
            this.store.dispatch(new ResetPersonalDetailsForm());
          } else {
            this.store.dispatch(new LoadPerson(this.clientCode, person.Id)).subscribe(() => {
              this.store.dispatch(new RefreshPersonalDetailsLists(this.clientCode));
              this.store.dispatch(new SetShowVerifyFederalId(false));
            });
          }

          if (reinit) {
            this.personalDetailsTab.ngOnInit();
          }

          if (!person.Id) {
            this._setNewPersonDefaults();
          }

          break;
        }
        case 'job-details-tab': {
          if (!person.Id) {
            this.store.dispatch(new ClearEmployeeStatuses());
            this.store.dispatch(new ResetJobDetailsForm());
          } else {
            this.store.dispatch(new LoadPerson(this.clientCode, person.Id)).subscribe(() => {
              this.store.dispatch(new RefreshEmployeeStatuses(this.clientCode));
            });
          }

          if (reinit) {
            this.jobDetailsTab.ngOnInit();
          }

          break;
        }
        case 'earnings-and-deductions-tab': {
          if (!person.Id) {
            this.store.dispatch(new SetEmployeeDeductions([]));
            this.store.dispatch(new SetEmployeeDeductions([]));
          } else {
            this.store.dispatch(new ReloadEmployeeDeductions(this.clientCode));
            this.store.dispatch(new ReloadEmployeeEarnings(this.clientCode));
          }
          break;
        }
        case 'taxes-tab': {
          this.taxesTab.selectByCounty = {}; // reset county/muni selectors
          if (!person.Id) {
            this.store.dispatch(new SetTaxExemptions());
            this.store.dispatch(new SetTaxes());
          } else {
            this.store.dispatch(new LoadPerson(this.clientCode, person.Id));
          }
          if (reinit) {
            this.taxesTab.ngOnInit();
          }
          break;
        }
        case 'direct-deposit-tab': {
          if (!person.Id) {
            this.store.dispatch(new SetDirectDeposits([]));
          } else {
            this.store.dispatch(new ReloadDirectDeposits());
          }
          break;
        }
      }
      this.store.dispatch(new MarkFormsPristine());
    });
  }

  public changeTabs(tabId: string) {
    this.store.selectOnce(PersonEditorState.getCurrentTabStatus).subscribe(status => {
      if (status.IsValid) {
        // if current form is valid, go to specified tab
        this.tabs.select(tabId);
        // set active tab number for the passed tab id
        this.activeTabIdx = +Object.keys(this.tabElemIds).find(
          key => this.tabElemIds[key] === tabId
        );
      } else {
        // mark all fields as touched so they display field level errors
        this.store.dispatch(new MarkActiveFormAsTouched());
        this.scrollToInvalidFormElement(this.tabOrder[this.activeTabIdx]);
      }
    });
  }

  public getNextTabId(): string {
    let tabNum = this.activeTabIdx;

    if (Object.keys(this.tabOrder).length <= this.activeTabIdx) {
      return null;
    } else {
      tabNum = this.activeTabIdx + 1;
    }

    // if this tab is disabled, keep looking until we find one that is enabled
    if (this.validationService.isTabDisabledAtIndex(tabNum, this.tabElemIds)) {
      let idx = tabNum;
      let found = false;
      while (Object.keys(this.tabOrder).length > idx) {
        idx++;
        if (!this.validationService.isTabDisabledAtIndex(idx, this.tabElemIds)) {
          tabNum = idx;
          found = true;
          break;
        }
      }

      // we didn't find another enabled tab
      if (!found) {
        tabNum = null;
      }
    }

    return tabNum ? this.tabElemIds[tabNum] : null;
  }

  // FIXME: move to validation service
  @Select(PersonEditorState.isPersonRecordComplete)
  public isPersonRecordComplete: Observable<boolean>;

  public onCancelClick() {
    // when in 'add' mode (creating a new person)
    // the cancel button will abandon changes and navigate to
    // either the payroll that they came from (addToPayroll query arg)
    // or the people list. the deactivate guard will confirm the navigation
    // change.
    if (this.mode == 'add') {
      this._goToReturnURL(false);
    } else {
      this.modalService.showConfirmModal(
        'You have unsaved changes. Are you sure you want to leave this page? Unsaved changes will be lost.',
        'Stay On Page',
        'Leave Page',
        'Are you sure you want to leave this page?'
      );

      this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
        if (confirm) {
          this.cancelChanges(true);
        }
      });
    }
  }

  // Save/Continue button clicked in 'add' mode
  //    save form if current form is valid & dirty and person record is 'complete'
  //    continue to next tab as long as form is valid. on last tab, if all forms are valid, go to people list
  public onContinueClick() {
    const nxt = this.getNextTabId();

    // there is no next tab
    this.store.selectOnce(PersonEditorState.isPersonRecordComplete).subscribe(isComplete => {
      this.store
        .selectOnce(PersonalDetailsFormSelectors.personalDetailsFormModel)
        .subscribe(personDetails => {
          this.store
            .selectOnce(PersonEditorState.getFederalIdChanged)
            .subscribe(federalIdChanged => {
              if (!nxt) {
                // we've reached the end of the tabs
                if (isComplete) {
                  // save the record
                  this.savePerson(true).subscribe();
                } else {
                  this.store.selectOnce(PersonEditorState.areFormsValid).subscribe(forms => {
                    let msg = '';
                    if (!forms.JobDetailsForm) {
                      msg = 'Required data missing from the Job Details tab';
                    } else if (!forms.TaxesForm) {
                      msg = 'Required data missing from the Taxes tab';
                    }
                    // the user skipped a tab that had required fields
                    this.modalService.showAlertModal(msg, ModalStates.Error);
                  });
                }
              } else {
                // if person record now has all required data, save
                if (isComplete) {
                  this.savePerson().subscribe(_ => {
                    // we have not yet reached the end of the tabs, go to the next tab
                    this.changeTabs(nxt);
                  });
                } else if (
                  federalIdChanged &&
                  personDetails.UseEIN != '1' &&
                  this.activeTabIdx == 1
                ) {
                  this._checkNewPersonDupeSSN(nxt);
                } else {
                  // we have not yet reached the end of the tabs, go to the next tab
                  this.changeTabs(nxt);
                }
              }
            });
        });
    });
  }

  public triggerSave(subject: Subject<any>) {
    this.store.selectOnce(PersonEditorState.isPersonRecordComplete).subscribe(isComplete => {
      this.store.selectOnce(PersonEditorState.areFormsPristine).subscribe(arePristine => {});
    });
  }

  public onDeleteClick() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      this.store
        .selectOnce(PersonEditorState.getPersonalDetailsFormModel)
        .subscribe(personDetails => {
          let msg = 'Are you sure you want to delete ';
          const f = person.FirstName || personDetails.FirstName;
          const l = person.LastName || personDetails.LastName;

          msg += f ? f + ' ' : '';
          msg += l || '';
          msg += (!f || !f.trim()) && (!l || !l.trim()) ? 'this person' : '';

          this.modalService.showConfirmModal(`${msg}?`, "Don't Delete", 'Delete this Person');
          this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
            if (confirm) {
              // if we have an existing person - hit api endpoint
              // else, just abandon changes and go back to people list
              const p = structuredClone(person);
              if (person.Id) {
                this.peopleService.deletePerson(this.clientCode, person.Id).subscribe(x => {
                  const skip = [
                    'FederalIdentifier',
                    'IntegrityHash',
                    'InvalidToPay',
                    'InvalidToPayDetails',
                    'LastPay',
                    'ClientCode'
                  ];
                  const deltas = getObjectDeltas(p, null, skip);
                  this.auditService.auditEmployee(
                    `employee-deleted`,
                    {
                      ClientCode: this.clientCode,
                      PersonId: person.Id,
                      EmployeeNumber: person.EmployeeId,
                      EmployeeName: `${person.LastName}, ${person.FirstName}`
                    },
                    deltas
                  );

                  this._goToReturnURL(false);
                });
              } else {
                this.resetForms();
                this._goToReturnURL(false);
              }
            }
          });
        });
    });
  }

  public onNavChange(evnt: NgbNavChangeEvent) {
    this.store.selectOnce(PersonEditorState.getReadOnly).subscribe(readOnly => {
      // do not present any on nav change warnings in read only mode
      if (readOnly) {
        return;
      }
      const obs: Observable<any>[] = [
        this.store.selectOnce(PersonEditorState.getDirectDepositFormDirty),
        this.store.selectOnce(PersonEditorState.getPerson),
        this.store.selectOnce(PersonEditorState.getFormModels),
        this.store.selectOnce(PersonEditorState.getCurrentTabStatus),
        this.store.selectOnce(PersonEditorState.getFederalIdChanged)
      ];
      forkJoin(obs).subscribe(result => {
        const ddFormDirty: boolean = result[0];
        const person: Person = result[1];
        const formModels: any = result[2];
        const currentTabStatus: any = result[3];
        const federalIdChanged: boolean = result[4];
        if (!currentTabStatus.IsValid) {
          // current form is invalid - stop tab change
          evnt.preventDefault();
          this.store.dispatch(new MarkActiveFormAsTouched());
          console.log('Navigation stopped due to invalid form');
        } else if (
          (currentTabStatus.IsDirty || (this.activeTabIdx == 6 && ddFormDirty)) &&
          this.mode === 'edit'
        ) {
          // confirm w user if they want to discard unsaved changes on current tab
          evnt.preventDefault();
          const msg =
            'You have unsaved changes. Are you sure you want to leave this page? Unsaved changes will be lost.';
          this.modalService.showConfirmModal(
            msg,
            'Stay On Page',
            'Leave Page',
            'Are you sure you want to leave this page?'
          );
          this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
            if (confirm) {
              // leave page and abandon changes
              this.cancelChanges(false);
              this.changeTabs(evnt.nextId);
            }
          });
        } else if (
          evnt.activeId === 'personal-details-tab' &&
          !formModels.PersonalDetails.FederalIdentifier &&
          !person.FederalIdentifier &&
          !this.confirmEmptyFederalId
        ) {
          // bug 5051 - if user is leaving the personal details tab w/o setting Federal Id - warning & confirm
          evnt.preventDefault();
          const useEIN = formModels.PersonalDetails.UseEIN;
          const msg = `Warning: ${
            useEIN ? 'Federal Tax ID Number' : 'Person SSN'
          } is missing. Are you sure you would like to continue?`;
          this.modalService.showConfirmModal(msg, 'Stay On Page', 'Leave Page');
          this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
            if (confirm) {
              // use confirmed they want to continue to the request tab w/o setting Federal Id
              this.confirmEmptyFederalId = true;
              this.changeTabs(evnt.nextId);
            }
          });
        } else if (
          evnt.activeId === 'personal-details-tab' &&
          formModels.PersonalDetails.FederalIdentifier &&
          federalIdChanged &&
          !this.checkedDupeSSN
        ) {
          // bug 5169
          // if user is on personal details tab && fed Id exists && fed id was changed
          // check to see if it is a dupe before letting user proceed
          evnt.preventDefault();
          const clone: any = structuredClone(formModels.PersonalDetails);
          clone.Birthday = parseDatePickerValue(clone.Birthday);
          const person = new Person();
          person.FirstName = clone.FirstName;
          person.LastName = clone.LastName;
          person.EmploymentTypeId = +clone.EmploymentTypeId;
          person.EmployeeId = clone.EmployeeId ?? 0;
          person.FederalIdentifier = clone.FederalIdentifier;
          person.UseEIN = clone.UseEIN == '1';
          this.companyService.checkDupeSSN(this.clientCode, person).subscribe(response => {
            const msg = FlattenResponseMessages(response.Messages);

            if (msg?.Body) {
              // warns a dupe SSN was caught, but employment types differ
              this.modalService.showAlertModal(msg, GetResponseModalStyle(response.Messages));
            }

            // send user to the requested tab
            this.checkedDupeSSN = true;
            this.changeTabs(evnt.nextId);
          });
        } else {
          this.activeTabIdx = Object.values(this.tabElemIds).indexOf(evnt.nextId) + 1;
          this.store.dispatch(new SetTabHasChanged());

          setTimeout(() => this.store.dispatch(new SetHeader()), 250);
        }

        if (evnt.nextId === 'personal-details-tab') {
          // reset bool that controls empty federal id confirmation modal on tab change
          this.confirmEmptyFederalId = false;
          this.checkedDupeSSN = false;
        }

        const employmentType = formModels.PersonalDetails.EmploymentTypeId;
        if (
          employmentType == 1 &&
          this.mode === 'add' &&
          evnt.nextId === 'taxes-tab' &&
          formModels.Validity.JobDetailsForm
        ) {
          // change continue button text when the record is ready to be saved on W-2 employees
          this.store.dispatch(new SetSaveButtonText('Save & Continue'));
        } else if (
          employmentType != 1 &&
          this.mode === 'add' &&
          evnt.nextId === 'job-details-tab' &&
          formModels.Validity.PersonalDetailsForm
        ) {
          // taxes tab does not need to be visisted for non W-2 employees
          this.store.dispatch(new SetSaveButtonText('Save & Continue'));
        }
      });
    });
  }

  // Save button clicked in 'edit' mode
  //    no 'continue' behavior. stay on current tab
  //    if current form is valid and dirty and person record is complete - save
  public onSaveClick() {
    this.store.selectOnce(PersonEditorState.getCurrentTabStatus).subscribe(status => {
      if (status.IsValid && status.IsDirty) {
        // all required fields have been met and form is dirty
        this.savePerson().subscribe(() => {
          this.toastService.show('Save successful');
        });
      } else if (status.IsValid && !status.IsDirty) {
        this.modalService.showAlertModal('No changes to save', ModalStates.Warning);
      } else if (!status.IsValid) {
        // mark as touched so any hidden field level errors appear
        this.store.dispatch(new MarkActiveFormAsTouched());
      }
    });
  }

  public resetForms() {
    this.store.dispatch(new ClearPerson()).subscribe(() => {
      this.store.dispatch(new ResetEditorForms());
      this.setFormsAsPristine();
    });
  }

  // FIXME: move to save person service
  public savePerson(redirectAfterSave: boolean = false): Observable<any> {
    return this.store.dispatch(new SetSaveInProgress(true)).pipe(
      mergeMap(() => {
        return this.store
          .dispatch(new SavePerson(this.clientCode, this.tabElemIds, this.tabs.activeId))
          .pipe(
            tap(() => {
              if (!redirectAfterSave) {
                this.store.dispatch(new SetSaveButtonText('Save & Continue'));
                this.store.dispatch(new SetSaveInProgress(false));
              } else {
                // in some scenarios, person is saved when continue btn is pressed on last tab
                // we need to save first, then redirect so this new person shows up in the list
                this._goToReturnURL(true);
              }
            }),
            catchError(err => {
              //this.resetForms();
              return of();
            })
          );
      })
    );
  }

  public setFormsAsPristine() {
    this.store.dispatch(new MarkFormsPristine());
    this.store.dispatch(new SetFederalIdChanged(false));
  }

  // #endregion

  // #region PRIVATE  METHODS

  private _checkNewPersonDupeSSN(nxt: string) {
    this.store.selectOnce(PersonalDetailsFormSelectors.personalDetailsFormModel).subscribe(f => {
      const person = new Person();
      person.FirstName = f.FirstName;
      person.LastName = f.LastName;
      person.EmploymentTypeId = +f.EmploymentTypeId;
      person.EmployeeId = 0;
      person.FederalIdentifier = f.FederalIdentifier;
      person.UseEIN = f.UseEIN == '1';
      this.companyService.checkDupeSSN(this.clientCode, person).subscribe(response => {
        const msg = FlattenResponseMessages(response.Messages);

        if (msg?.Body) {
          // warns a dupe SSN was caught, but employment types differ
          this.modalService.showAlertModal(msg, GetResponseModalStyle(response.Messages));
        }

        if (!response.Messages.find(x => x.WarningLevel == WarningLevel.Critical)) {
          this.changeTabs(nxt);
        }
      });
    });
  }

  // in read only mode, disable all form controls
  // and remove any validators
  private _disableAllFormControls() {
    Object.keys(this.tabOrder).forEach(key => {
      let group = this.tabOrder[key] as UntypedFormGroup;
      this.disableAllFormControls(group);
    });
  }

  private _goToReturnURL(addPerson: boolean) {
    // task 9640. take user back to payroll if that's how they
    // got here (check the addToPayroll query param)
    if (this.addToPayrollRoute && addPerson) {
      // task 9637
      // this person was successfully created
      // fire API call to add them to the existing payroll
      const info = this.sharedService.getPayrollStatusInfo();

      if (!info) {
        // no payroll status info was found in session storage.
        // possibly reasons may include:
        //  *) user bookmarked this url after they chose 'create person'
        //     from payroll. then closed their browsers and returned to it later
        //  *) user copied this url and opened it in a new browser
        // these sorts of user behaviors would break them out of the normal
        // 'create a person, add them to the payroll, then take me back to payroll' flow.
        // the result will be they get taken back to payroll, but have to
        // either manually add the person to the payroll,
        //  or cancel and start payroll again.
        this.returnToPayroll.emit({ route: this.addToPayrollRoute });
      } else {
        this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
          // add the person to the current payroll
          this.payrollService
            .addPerson(info.ClientCode, info.Year, info.SequenceNumber, person.Id)
            .subscribe(
              resp => {
                // add query param to payroll route so that new row
                // is focused when the payroll grid loads
                this.returnToPayroll.emit({
                  route: this.addToPayrollRoute,
                  selectedEmployee: person.Id
                });
              },
              err => {
                // there was an error adding the person to payroll,
                // likely because they are invalid to pay for some reason.
                // error interceptor will display the error message and
                // here we navigate the user back to payroll
                this.returnToPayroll.emit({ route: this.addToPayrollRoute });
              }
            );
        });
      }
    } else if (this.addToPayrollRoute && !addPerson) {
      // person record was cancelled, just take back to payroll
      this.returnToPayroll.emit({ route: this.addToPayrollRoute });
    } else {
      // editor finished
      this.onEditorLeave.emit();
    }
  }

  private _init() {
    this.store.dispatch(new SetEditorMode(this.mode)).subscribe(() => {
      if (this.isEmployeePortal) {
        const whitelistChecks = [
          this.whitelistService.checkWhitelistForFeature(this.clientCode, 'addressChange'),
          this.whitelistService.checkWhitelistForFeature(this.clientCode, 'directDepositChange')
        ];
        forkJoin(whitelistChecks).subscribe(whitelist => {
          if (whitelist[0] || whitelist[1]) {
            this.changeRequestService.canEnableChangeRequests(this.clientCode).subscribe(result => {
              this.canSubmitRequests = result.Value;
            });
          }
        });
      }
      // build forms, get enums, company configs, person data
      this.forms = this.initService.buildForms();
      this.tabOrder = this.initService.setTabOrder(this.forms);
      this.store
        .dispatch(new LoadUniversalEnums(this.clientCode))
        .pipe(mergeMap(() => this._initCompanyAndPerson()))
        .subscribe();
      this.store
        .select(PersonEditorState.getEmployeeState)
        .pipe(takeUntil(this.destroy$))
        .subscribe(state => {
          if (state != null) {
            this.store.dispatch(new BuildLocalTaxForm());
          }
        });
      this.store
        .select(PersonEditorState.getEmployeeWorksIn)
        .pipe(takeUntil(this.destroy$))
        .subscribe(worksIn => {
          if (worksIn != null) {
            this.store.dispatch(new BuildLocalTaxForm()).subscribe(() => {
              this.store.dispatch(new SetEmployeeStateTaxExemptionsByState());
            });
          }
        });
    });
  }

  private _initCompanyAndPerson() {
    return this.store.dispatch(new LoadUniversalCompanyConfigs(this.clientCode)).pipe(
      mergeMap(() => {
        return this.companyService.getLock(this.clientCode).pipe(
          mergeMap(resp => {
            // if client is locked for any reason
            // set person editor to read only mode.
            this._setClientLock(resp).subscribe(() => {});

            if (this.mode == 'edit') {
              return this.store.dispatch(new LoadPerson(this.clientCode, this.personId)).pipe(
                tap(() => {
                  this.initComplete = true;
                })
              );
            } else {
              // user is creating a new person
              this._setNewPersonDefaults();
              const obs = [
                this.store.dispatch(new LoadPersonalDetailsForm()),
                this.store.dispatch(new LoadJobDetailsForm()),
                this.store.dispatch(new SetTaxes()),
                this.store.dispatch(new SetTaxExemptions()),
                this.store.dispatch(new SetHeader())
              ];
              return forkJoin(obs).pipe(
                tap(() => {
                  this.initComplete = true;
                })
              );
            }
          })
        );
      })
    );
  }

  private _setClientLock(resp: ApiResponse<CompanyLock>): Observable<boolean> {
    // task 9639. do not disable create person functionality
    // if the client is locked due to payroll in progress and
    // the logged in user is the one that holds the lock.
    this.companyLock = resp?.Model;

    const lockTypeMatch =
      resp?.Model?.LockType == LockTypes.Payroll ||
      resp?.Model?.LockType == LockTypes['Payroll Held'];

    // if client is locked due to payroll,
    // allow user to create new person
    // if it's the same person that started the payroll
    if (
      this.mode == 'add' &&
      lockTypeMatch &&
      this.authService.isCurrentUserFirstTen(resp?.Model?.LegacyUserId)
    ) {
      // forms are fully editable
      this.store.dispatch(new SetReadOnly(false));
      return of(null);
    } else if (!this.editViaPayrollGrid) {
      // forms are ready only if we got any response back
      // from the client lock endpoint
      return this.store.dispatch(new SetReadOnly(resp && resp.Model != null)).pipe(
        mergeMap(() => {
          return this.store.selectOnce(PersonEditorState.getReadOnly).pipe(
            tap(readOnly => {
              if (readOnly) {
                this._disableAllFormControls();
              }
            })
          );
        })
      );
    }
    return of();
  }
  // load model data into reactive forms
  // FIXME: move to init service

  // FIXME: move to init service
  private _setNewPersonDefaults() {
    this.forms.PersonalDetails.patchValue({
      EmploymentTypeId: EmploymentTypes.W2.toString()
    });
  }

  // #endregion
}
